import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../../config/api';
import ImageUpload from '../../components/ImageUpload';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
// Remove: import { Dialog } from '@headlessui/react';

const RentalListing = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [product, setProduct] = useState({});
  const [imageUrls, setImageUrls] = useState([]);
  const [search, setSearch] = useState('');
  const [editForm, setEditForm] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [prodRes, catRes] = await Promise.all([
          fetch(getApiUrl('/api/admin/products'), { headers: { 'x-admin': 'true' } }),
          fetch(getApiUrl('/api/admin/categories'), { headers: { 'x-admin': 'true' } })
        ]);
        const prodData = await prodRes.json();
        const catData = await catRes.json();
        let productsList = Array.isArray(prodData.products) ? prodData.products : Array.isArray(prodData) ? prodData : [];
        let categoriesList = Array.isArray(catData.categories) ? catData.categories : Array.isArray(catData) ? catData : [];
        // Find the rental category id
        const rentalCategory = categoriesList.find(cat => cat.name && cat.name.toLowerCase() === 'rental');
        if (rentalCategory) {
          productsList = productsList.filter(prod => {
            const catId = prod.category?._id || prod.category;
            return catId === rentalCategory._id;
          });
        } else {
          productsList = [];
        }
        setProducts(productsList);
        setCategories(categoriesList);
        setLoading(false);
      } catch (err) {
        setError('Failed to load rental products');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter by search
  const filteredProducts = products.filter(prod =>
    (prod.name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Get token for admin actions
  const token = localStorage.getItem('token');

  // Delete rental product
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rental product?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/admin/products/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-admin': 'true'
        },
      });
      if (!res.ok) throw new Error('Failed to delete rental product');
      setProducts(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Approve rental
  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/products/rentals/${id}/approve`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-admin': 'true'
        },
      });
      if (!res.ok) throw new Error('Failed to approve rental');
      const data = await res.json();
      setProducts(prev => prev.map(p => p._id === id ? { ...p, rentalStatus: 'approved' } : p));
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Reject rental
  const handleReject = async (id) => {
    setActionLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/products/rentals/${id}/reject`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-admin': 'true'
        },
      });
      if (!res.ok) throw new Error('Failed to reject rental');
      const data = await res.json();
      setProducts(prev => prev.map(p => p._id === id ? { ...p, rentalStatus: 'rejected' } : p));
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Edit rental product
  const handleEdit = (product) => {
    setEditForm({
      ...product,
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      securityDeposit: product.securityDeposit || '',
      rentalImages: product.rentalImages || [],
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/admin/products/${editForm._id}`), {
        method: 'PUT',
        headers: {
          'x-admin': 'true',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editForm.name,
          description: editForm.description,
          price: editForm.price,
          securityDeposit: editForm.securityDeposit,
          rentalImages: editForm.rentalImages,
          isRental: true,
        }),
      });
      if (!res.ok) throw new Error('Failed to update rental product');
      const updated = await res.json();
      setProducts(prev => prev.map(p => p._id === editForm._id ? { ...p, ...updated } : p));
      setEditModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Rental Listing</h1>
      </div>
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search rental products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts.map(product => (
              <tr key={product._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {product.rentalImages && product.rentalImages.length > 0 ? (
                    <img
                      src={product.rentalImages[0]}
                      alt={product.name}
                      className="h-12 w-12 object-cover rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded"></div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{product.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">₹{product.price}</td>
                <td className="px-6 py-4 whitespace-nowrap">{product.owner?.name || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap">{product.rentalStatus || 'pending'}</td>
                <td className="px-6 py-4 whitespace-nowrap flex gap-2 items-center">
                  <button className="text-blue-500 hover:text-blue-700 mr-2" onClick={() => handleEdit(product)} disabled={actionLoading}>
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button className="text-red-500 hover:text-red-700 mr-2" onClick={() => handleDelete(product._id)} disabled={actionLoading}>
                    <TrashIcon className="w-5 h-5" />
                  </button>
                  {product.rentalStatus !== 'approved' && (
                    <button className="bg-green-500 text-white px-2 py-1 rounded text-xs" onClick={() => handleApprove(product._id)} disabled={actionLoading}>Approve</button>
                  )}
                  {product.rentalStatus !== 'rejected' && (
                    <button className="bg-red-500 text-white px-2 py-1 rounded text-xs" onClick={() => handleReject(product._id)} disabled={actionLoading}>Reject</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto p-6 z-10">
            <div className="text-xl font-bold mb-4 text-primary">Edit Rental Product</div>
            <form className="space-y-3" onSubmit={e => { e.preventDefault(); handleEditSave(); }}>
              <input type="text" name="name" value={editForm.name || ''} onChange={handleEditFormChange} className="border rounded px-2 py-1 w-full" placeholder="Name" required />
              <textarea name="description" value={editForm.description || ''} onChange={handleEditFormChange} className="border rounded px-2 py-1 w-full" placeholder="Description" required />
              <input type="number" name="price" value={editForm.price || ''} onChange={handleEditFormChange} className="border rounded px-2 py-1 w-full" placeholder="Price" required />
              <input type="number" name="securityDeposit" value={editForm.securityDeposit || ''} onChange={handleEditFormChange} className="border rounded px-2 py-1 w-full" placeholder="Security Deposit" required />
              {editForm.rentalImages && editForm.rentalImages.length > 0 && (
                <img src={editForm.rentalImages[0]} alt="Current" className="h-20 w-20 object-cover rounded mb-2" />
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" className="px-4 py-2 border rounded text-gray-700" onClick={() => setEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded" disabled={actionLoading}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalListing; 